import './ImageGallery.css';
import ImageSquare from "./ImageSquare/ImageSquare.jsx";
import {useEffect, useRef, useState} from "react";

export default function ImageGallery({ images = [] }) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const thumbsContainerRef = useRef(null); // inner scroller
    const thumbRefs = useRef([]);

    // Touch swipe for main image
    const swipeStartX = useRef(null);
    const handleTouchStart = (e) => {
        swipeStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
        if (swipeStartX.current == null) return;
        const diffX = swipeStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diffX) > 50) {
            setCurrentImageIndex((i) => {
                if (diffX > 0) return Math.min(i + 1, images.length - 1);
                return Math.max(i - 1, 0);
            });
        }
        swipeStartX.current = null;
    };

    const manageImageChange = (value, operation) => {
        if (operation === 'next') {
            setCurrentImageIndex((i) => Math.min(i + 1, images.length - 1));
        } else if (operation === 'prev') {
            setCurrentImageIndex((i) => Math.max(i - 1, 0));
        } else if (operation === 'change') {
            setCurrentImageIndex(value);
        }
    };

    // Keep active thumbnail in view (center-ish)
    useEffect(() => {
        const el = thumbRefs.current[currentImageIndex];
        if (el?.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
    }, [currentImageIndex]);

    // Prevent ghost-drag on images
    useEffect(() => {
        const container = document.getElementById('image-gallery-container');
        if (!container) return;
        container.querySelectorAll('img').forEach(img => img.setAttribute('draggable', 'false'));
    }, []);

    if (!images.length) return null;

    return (
        <div id='image-gallery-container'>
            <div id='image-gallery-top'>
                <button
                    id='image-gallery-top-bttn-prev'
                    className='image-gallery-top-bttn'
                    onClick={() => manageImageChange(null, 'prev')}
                    aria-label="Previous image"
                >
                    <img loading="lazy"
                         src={currentImageIndex > 0 ? '/images/arrow-icon.webp' : '/images/arrow-icon-inactive.webp'}
                         alt="" />
                </button>

                <div
                    id='image-gallery-main-container'
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    {images.map((image, index) => (
                        <div
                            className='image-gallery-main-image'
                            key={index}
                            style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                        >
                            <img loading="lazy" src={image.url} alt={image.alt || `Zdjęcie ${index + 1}`} />
                        </div>
                    ))}
                </div>

                <button
                    id='image-gallery-top-bttn-next'
                    className='image-gallery-top-bttn'
                    onClick={() => manageImageChange(null, 'next')}
                    aria-label="Next image"
                >
                    <img loading="lazy"
                         src={currentImageIndex < images.length - 1 ? '/images/arrow-icon.webp' : '/images/arrow-icon-inactive.webp'}
                         alt="" />
                </button>
            </div>

            {/* HARD CLIP WRAPPER (overflow hidden) */}
            <div id='image-gallery-bottom-wrap'>
                <div id='image-gallery-bottom' ref={thumbsContainerRef}>
                    {images.map((image, index) => (
                        <ImageSquare
                            alt={image.alt}
                            key={index}
                            refCb={(el) => (thumbRefs.current[index] = el)}
                            image={image.url}
                            index={index}
                            currentImageIndex={currentImageIndex}
                            length={images.length}
                            isActive={index === currentImageIndex}
                            manageImageChange={manageImageChange}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
